import React, { useEffect, useState } from 'react';
import { BarChart3, TrendingUp } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_LABELS = {
  listen_live: 'Listen Live',
  song_request_submit: 'Song requests',
  music_submit: 'Music submissions',
  prayer_submit: 'Prayer wall posts',
  prayer_react: 'Prayer reactions',
  share_now_playing: 'Now playing shares',
  cast_open: 'Cast / AirPlay opened',
  sleep_timer_set: 'Sleep timers set',
  reminder_set: 'On-air reminders',
  favorite_show: 'Show favorited',
  language_switch: 'Language switched',
  listen_episode: 'Catch-up plays',
};

const RANGES = [
  { key: '24h', days: 1, label: '24h' },
  { key: '7d', days: 7, label: '7d' },
  { key: '30d', days: 30, label: '30d' },
];

function Sparkline({ trend }) {
  if (!trend?.length) return null;
  const w = 260;
  const h = 60;
  const pad = 4;
  const max = Math.max(1, ...trend.map(t => t.count));
  const step = trend.length > 1 ? (w - pad * 2) / (trend.length - 1) : 0;
  const points = trend.map((t, i) => {
    const x = pad + i * step;
    const y = h - pad - (t.count / max) * (h - pad * 2);
    return [x, y];
  });
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
  const area = `${path} L ${points[points.length - 1][0].toFixed(1)} ${h - pad} L ${points[0][0].toFixed(1)} ${h - pad} Z`;
  return (
    <svg width="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="block">
      <defs>
        <linearGradient id="cvSparkFill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.32" />
          <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#cvSparkFill)" />
      <path d={path} fill="none" stroke="#D4AF37" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      {points.map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r={i === points.length - 1 ? 3 : 1.4} fill="#D4AF37" />
      ))}
    </svg>
  );
}

function formatAxisLabel(bucket, kind, i, count) {
  // Only render a handful of ticks so 24 / 30 don't overcrowd.
  if (kind === 'hour') {
    if (![0, 6, 12, 18, count - 1].includes(i)) return '';
    try {
      const d = new Date(bucket + ':00:00Z');
      return d.toLocaleTimeString(undefined, { hour: '2-digit', hour12: false, timeZone: 'UTC' });
    } catch { return ''; }
  }
  // day
  const showEvery = count <= 7 ? 1 : Math.ceil(count / 6);
  if (i !== count - 1 && i % showEvery !== 0) return '';
  try {
    const d = new Date(bucket + 'T00:00:00Z');
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', timeZone: 'UTC' });
  } catch { return ''; }
}

export default function EngagementCard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rangeKey, setRangeKey] = useState('7d');

  useEffect(() => {
    const range = RANGES.find(r => r.key === rangeKey) || RANGES[1];
    setLoading(true);
    axios.get(`${API}/admin/analytics/summary?days=${range.days}`)
      .then(r => { setData(r.data); setError(false); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [rangeKey]);

  if (error) return null;

  const total = data?.total_events || 0;
  const events = data?.events || {};
  const top = Object.entries(events).sort((a, b) => b[1].total - a[1].total).slice(0, 4);
  const trend = data?.trend || [];
  const trendKind = data?.trend_kind || 'day';

  return (
    <div data-testid="engagement-card" className="bg-[#171A21] rounded-2xl border border-white/5 p-6">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-xl bg-[#D4AF37]/10">
            <BarChart3 size={18} className="text-[#D4AF37]" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">Engagement</h2>
            <p className="text-xs text-[#94A3B8]">Consented events</p>
          </div>
        </div>
        <div className="text-right">
          <p className={`text-2xl font-semibold text-white leading-none tabular-nums ${loading ? 'opacity-50' : ''}`}>{total}</p>
          <p className="text-[10px] uppercase tracking-widest text-[#94A3B8] mt-1">events</p>
        </div>
      </div>

      {/* Range chips */}
      <div data-testid="engage-range-chips" className="inline-flex bg-white/5 rounded-full p-1 mb-4">
        {RANGES.map(r => {
          const active = r.key === rangeKey;
          return (
            <button
              key={r.key}
              type="button"
              data-testid={`engage-range-${r.key}`}
              onClick={() => setRangeKey(r.key)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold transition-colors ${active ? 'bg-[#D4AF37] text-[#0F1115]' : 'text-[#94A3B8] hover:text-white'}`}
            >
              {r.label}
            </button>
          );
        })}
      </div>

      {/* Trend */}
      <div className="mb-5">
        <Sparkline trend={trend} />
        <div className="flex justify-between mt-1.5 text-[10px] text-[#64748B]">
          {trend.map((d, i) => {
            const label = formatAxisLabel(d.bucket, trendKind, i, trend.length);
            const isLast = i === trend.length - 1;
            return (
              <span key={d.bucket} className={isLast && label ? 'text-[#D4AF37] font-semibold' : ''}>{label}</span>
            );
          })}
        </div>
      </div>

      {/* Top events */}
      {top.length === 0 ? (
        <div className="flex items-center gap-2 text-xs text-[#94A3B8] bg-white/[0.03] rounded-xl p-4">
          <TrendingUp size={14} className="text-[#D4AF37]" />
          Waiting for listener activity. Events appear here once listeners tap Accept all on the cookie banner.
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-widest text-[#94A3B8]">Top events</p>
          {top.map(([name, counts]) => {
            const pct = total > 0 ? Math.max(4, Math.round((counts.total / total) * 100)) : 0;
            return (
              <div key={name} data-testid={`engage-event-${name}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-white/90">{EVENT_LABELS[name] || name}</span>
                  <span className="text-[#94A3B8] tabular-nums">
                    {counts.total}
                    <span className="text-[#64748B]"> · web {counts.web} · mob {counts.mobile}</span>
                  </span>
                </div>
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-[#D4AF37]/70" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
